/**
 * Maestro chat service.
 *
 * Purpose: Provides API access to backend chat proxy endpoints.
 * Architecture: Frontend service layer for /chat endpoints on core-api.
 * Dependencies: apiService and chat types.
 */

import apiService from './api';
import { env } from '../config/env';
import {
  BackendApiResponse,
  BackendChatMessage,
  ChatMessagesData,
  ChatModelCatalog,
  ChatModelOption,
  ChatSessionSummary,
  BackendGenerateData,
  BackendOrchestrateResumeData,
  FlightTimelineBuildRequestPayload,
  FlightTimelineBuildResponse,
  LlmGenerateRequest,
  ToolEvent,
} from '../types/chat';

/**
 * A streaming fetch with the session's token, renewed once on a 401 the way the
 * axios client renews it (access tokens last 15 minutes, ORCH-356).
 */
export const fetchWithSession = async (url: string, init: RequestInit): Promise<Response> => {
  const send = (token: string | null) =>
    fetch(url, {
      ...init,
      headers: {
        ...(init.headers as Record<string, string> | undefined),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  const response = await send(localStorage.getItem('orchestratehq_token'));
  if (response.status !== 401) return response;
  const renewed = await apiService.renewSession();
  return renewed ? send(renewed) : response;
};

const mapBackendMessage = (message: BackendChatMessage): BackendChatMessage => ({
  ...message,
  llm_payload: message.llm_payload ?? null,
});

/**
 * Find the next complete SSE block. Servers may use `\n\n` (LF) or `\r\n\r\n` (CRLF);
 * splitting only on `\n\n` misses CRLF-framed events, so nothing dispatches until EOF.
 */
function takeNextSseBlock(buffer: string): { block: string; rest: string } | null {
  const crlf = buffer.indexOf('\r\n\r\n');
  const lf = buffer.indexOf('\n\n');
  let sep = -1;
  let sepLen = 2;
  if (crlf === -1) {
    sep = lf;
    sepLen = 2;
  } else if (lf === -1) {
    sep = crlf;
    sepLen = 4;
  } else {
    sep = Math.min(crlf, lf);
    sepLen = sep === crlf ? 4 : 2;
  }
  if (sep === -1) return null;
  return { block: buffer.slice(0, sep).trim(), rest: buffer.slice(sep + sepLen) };
}

export const maestroChatService = {
  async generateStream(
    payload: LlmGenerateRequest,
    handlers: {
      onMeta?: (data: {
        session_id?: string;
        assistant_message_id?: string;
        run_id?: string;
        thread_id?: string;
      }) => void;
      onContentDelta?: (data: { text: string }) => void;
      onToolEvent?: (data: ToolEvent & { model?: string }) => void;
      onToolCalled?: (data: {
        run_id: string;
        iteration: number;
        tool_name: string;
        tool_args_summary: string;
        tool_call_id: string;
        phase?: string;
        step_id?: string;
      }) => void;
      onToolResult?: (data: {
        run_id: string;
        iteration: number;
        tool_name: string;
        tool_call_id: string;
        status: string;
        result_summary: string;
        ui_action?: { type: string; payload?: Record<string, unknown> } | null;
      }) => void;
      /**
       * Stage of the run that just started (ORCH-406): a stable key such as
       * `brief_extract`; the panel's `PROGRESS_LABELS` owns the wording.
       */
      onProgress?: (data: { stage: string; run_id?: string; step_id?: string }) => void;
      onAgentThinking?: (data: { run_id: string; iteration: number; message: string }) => void;
      onApprovalRequired?: (data: Record<string, unknown>) => void;
      onPhaseStarted?: (data: { phase: string; detail: string; step_id?: string }) => void;
      /**
       * Orchestrator ReAct subgraph LLM tokens (chain-of-thought stream).
       * `step_id` names the workflow step the delta belongs to (ORCH-286 P6);
       * `phase` stays the agent-level label and is absent from neither.
       */
      onCotDelta?: (data: { text: string; phase?: string; step_id?: string }) => void;
      onConfidenceScores?: (data: Record<string, number>) => void;
      onFinal?: (data: BackendGenerateData) => void;
      onStatus?: (data: { phase: string; [key: string]: unknown }) => void;
    }
  ): Promise<BackendGenerateData> {
    const formData = new FormData();
    formData.append('prompt', payload.prompt);
    if (payload.context) formData.append('context', payload.context);
    if (payload.selected_models?.length) {
      formData.append('selected_models', JSON.stringify(payload.selected_models));
    }
    formData.append('enable_web_search', String(payload.enable_web_search ?? true));
    if (payload.session_id) formData.append('session_id', payload.session_id);
    if (payload.selected_plan_model)
      formData.append('selected_plan_model', payload.selected_plan_model);
    if (payload.selected_plan_context)
      formData.append('selected_plan_context', payload.selected_plan_context);
    if (payload.media_brief_file) formData.append('media_brief_file', payload.media_brief_file);
    if (typeof payload.timeline_rendered === 'boolean') {
      formData.append('timeline_rendered', String(payload.timeline_rendered));
    }
    if (payload.budget_allocation_context) {
      formData.append(
        'budget_allocation_context',
        JSON.stringify(payload.budget_allocation_context)
      );
    }
    if (payload.mode) {
      formData.append('mode', payload.mode);
    }
    if (payload.planning_entity_id) {
      formData.append('planning_entity_id', payload.planning_entity_id);
    }
    if (payload.human_approval_mode) {
      formData.append('human_approval_mode', payload.human_approval_mode);
    }
    if (typeof payload.continue_past_brief === 'boolean') {
      formData.append('continue_past_brief', String(payload.continue_past_brief));
    }
    if (payload.orchestrator_thread_id) {
      formData.append('orchestrator_thread_id', payload.orchestrator_thread_id);
    }
    if (payload.orchestrator_entry) {
      formData.append('orchestrator_entry', payload.orchestrator_entry);
    }

    const response = await fetchWithSession(`/planning-api/v1/generate/stream`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok || !response.body) {
      throw new Error(`Streaming request failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let finalData: BackendGenerateData | null = null;

    const yieldForFinalAnswerPaint = () =>
      new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 12);
      });

    const dispatch = async (eventName: string, payloadData: any) => {
      if (eventName === 'run_failed') {
        const err =
          typeof payloadData?.error === 'string' && payloadData.error.trim()
            ? payloadData.error
            : 'Run failed';
        throw new Error(err);
      }
      if (eventName === 'meta') handlers.onMeta?.(payloadData);
      if (eventName === 'content_delta') {
        handlers.onContentDelta?.(payloadData);
        await yieldForFinalAnswerPaint();
        return;
      }
      if (eventName === 'tool_event') handlers.onToolEvent?.(payloadData);
      if (eventName === 'tool_called') handlers.onToolCalled?.(payloadData);
      if (eventName === 'tool_result') handlers.onToolResult?.(payloadData);
      if (eventName === 'progress') handlers.onProgress?.(payloadData);
      if (eventName === 'agent_thinking') handlers.onAgentThinking?.(payloadData);
      if (eventName === 'run_started')
        handlers.onStatus?.({ phase: 'run_started', ...payloadData });
      if (eventName === 'run_completed')
        handlers.onStatus?.({ phase: 'run_completed', ...payloadData });
      if (eventName === 'status') handlers.onStatus?.(payloadData);
      if (eventName === 'approval_required') handlers.onApprovalRequired?.(payloadData);
      if (eventName === 'phase_started') handlers.onPhaseStarted?.(payloadData);
      if (eventName === 'cot_delta') handlers.onCotDelta?.(payloadData);
      if (eventName === 'confidence_scores') handlers.onConfidenceScores?.(payloadData);
      if (eventName === 'final') {
        finalData = payloadData as BackendGenerateData;
        handlers.onFinal?.(finalData);
      }
      if (eventName === 'error') {
        throw new Error('Sorry, something went wrong. Please try again.');
      }
    };

    const processChunk = async (chunk: string) => {
      const lines = chunk.split(/\r?\n/);
      let eventName = 'message';
      const dataLines: string[] = [];
      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trim());
        }
      }
      if (!dataLines.length) return;
      const payloadText = dataLines.join('\n');
      const payloadData = payloadText ? JSON.parse(payloadText) : {};
      await dispatch(eventName, payloadData);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let next: { block: string; rest: string } | null;
      while ((next = takeNextSseBlock(buffer))) {
        buffer = next.rest;
        if (next.block) await processChunk(next.block);
      }
    }
    buffer += decoder.decode();

    const tail = buffer.trim();
    if (tail) {
      try {
        await processChunk(tail);
      } catch {
        /* ignore incomplete trailing SSE block */
      }
    }

    if (!finalData) {
      throw new Error('Streaming response ended before final payload.');
    }
    const resolvedFinalData = finalData as BackendGenerateData;
    return {
      ...resolvedFinalData,
      user_message: mapBackendMessage(resolvedFinalData.user_message),
      assistant_message: mapBackendMessage(resolvedFinalData.assistant_message),
    };
  },

  async orchestrateResumeStream(
    body: { thread_id: string; session_id: string; resume: Record<string, unknown> },
    handlers: {
      onMeta?: (data: { session_id?: string; assistant_message_id?: string }) => void;
      onContentDelta?: (data: { text: string }) => void;
      onCotDelta?: (data: { text: string; phase?: string; step_id?: string }) => void;
      onStatus?: (data: { phase: string; [key: string]: unknown }) => void;
      onPhaseStarted?: (data: { phase: string; detail: string; step_id?: string }) => void;
      onApprovalRequired?: (data: Record<string, unknown>) => void;
      onToolCalled?: (data: {
        run_id: string;
        iteration: number;
        tool_name: string;
        tool_args_summary: string;
        tool_call_id: string;
        phase?: string;
        step_id?: string;
      }) => void;
      onToolResult?: (data: {
        run_id: string;
        iteration: number;
        tool_name: string;
        tool_call_id: string;
        status: string;
        result_summary: string;
        ui_action?: { type: string; payload?: Record<string, unknown> } | null;
      }) => void;
      /**
       * Stage of the run that just started (ORCH-406): a stable key such as
       * `brief_extract`; the panel's `PROGRESS_LABELS` owns the wording.
       */
      onProgress?: (data: { stage: string; run_id?: string; step_id?: string }) => void;
      onFinal?: (data: BackendOrchestrateResumeData) => void;
    }
  ): Promise<BackendOrchestrateResumeData> {
    const response = await fetchWithSession(`/planning-api/v1/generate/orchestrate-resume/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok || !response.body) {
      throw new Error(`Orchestrate resume failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let finalData: BackendOrchestrateResumeData | null = null;
    let sawApprovalOrInterrupt = false;
    let sawAnyEvent = false;

    const yieldForFinalAnswerPaint = () =>
      new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 12);
      });

    const dispatch = async (eventName: string, payloadData: any) => {
      sawAnyEvent = true;
      if (eventName === 'run_failed') {
        const err =
          typeof payloadData?.error === 'string' && payloadData.error.trim()
            ? payloadData.error
            : 'Run failed';
        throw new Error(err);
      }
      if (eventName === 'meta') handlers.onMeta?.(payloadData);
      if (eventName === 'content_delta') {
        handlers.onContentDelta?.(payloadData);
        await yieldForFinalAnswerPaint();
        return;
      }
      if (eventName === 'status') handlers.onStatus?.(payloadData);
      if (eventName === 'phase_started') handlers.onPhaseStarted?.(payloadData);
      if (eventName === 'cot_delta') handlers.onCotDelta?.(payloadData);
      if (eventName === 'tool_called') handlers.onToolCalled?.(payloadData);
      if (eventName === 'tool_result') handlers.onToolResult?.(payloadData);
      if (eventName === 'progress') handlers.onProgress?.(payloadData);
      if (eventName === 'approval_required') {
        sawApprovalOrInterrupt = true;
        handlers.onApprovalRequired?.(payloadData);
      }
      if (eventName === 'final') {
        finalData = payloadData as BackendOrchestrateResumeData;
        handlers.onFinal?.(finalData);
      }
      if (eventName === 'error') {
        throw new Error('Sorry, something went wrong. Please try again.');
      }
    };

    const processChunk = async (chunk: string) => {
      const lines = chunk.split(/\r?\n/);
      let eventName = 'message';
      const dataLines: string[] = [];
      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trim());
        }
      }
      if (!dataLines.length) return;
      const payloadText = dataLines.join('\n');
      const payloadData = payloadText ? JSON.parse(payloadText) : {};
      await dispatch(eventName, payloadData);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let next: { block: string; rest: string } | null;
      while ((next = takeNextSseBlock(buffer))) {
        buffer = next.rest;
        if (next.block) await processChunk(next.block);
      }
    }
    buffer += decoder.decode();
    const tail = buffer.trim();
    if (tail) {
      try {
        await processChunk(tail);
      } catch {
        /* ignore */
      }
    }

    if (!finalData) {
      // Treat EOF without final as an error unless we *know* the orchestrator intentionally
      // interrupted (approval_required). Silent stubs make “random stoppage” look like success.
      if (!sawApprovalOrInterrupt) {
        throw new Error(
          sawAnyEvent
            ? 'Resume stream ended unexpectedly before final payload.'
            : 'Resume stream ended without any events.'
        );
      }
      // As a fallback for old servers that may end on interrupt without final.
      return {
        session_id: body.session_id,
        assistant_message: {
          message_id: '',
          session_id: body.session_id,
          role: 'assistant' as const,
          content: '',
          llm_payload: null,
          created_at: new Date().toISOString(),
        },
        llm_response: {
          final_answer: '',
          selected_models: [],
          orchestrator_interrupted: true,
        } as any,
      };
    }
    const resolved = finalData as BackendOrchestrateResumeData;
    return {
      session_id: resolved.session_id,
      assistant_message: mapBackendMessage(resolved.assistant_message),
      llm_response: resolved.llm_response,
    };
  },

  // PR-D2 URL fix: use apiService.api directly with `{ baseURL: '' }` so axios
  // does not prepend the `/core-api` baseURL on routes that target planning service.
  async listSessions(): Promise<ChatSessionSummary[]> {
    const response = await apiService.api.get<BackendApiResponse<ChatSessionSummary[]>>(
      '/planning-api/v1/sessions',
      { baseURL: '' }
    );
    return response.data.data;
  },

  async getSessionMessages(sessionId: string): Promise<ChatMessagesData> {
    const response = await apiService.api.get<BackendApiResponse<ChatMessagesData>>(
      `/planning-api/v1/sessions/${sessionId}/messages`,
      { baseURL: '' }
    );
    return {
      ...response.data.data,
      messages: response.data.data.messages.map(mapBackendMessage),
    };
  },

  async deleteSession(sessionId: string): Promise<void> {
    await apiService.api.delete(`/planning-api/v1/sessions/${sessionId}`, { baseURL: '' });
  },

  async listModels(): Promise<ChatModelCatalog> {
    const response = await apiService.api.get<ChatModelCatalog>('/planning-api/v1/models', {
      baseURL: '',
    });
    return response.data;
  },

  async buildFlightTimeline(
    payload: FlightTimelineBuildRequestPayload & { session_id?: string }
  ): Promise<FlightTimelineBuildResponse> {
    const response = await apiService.api.post<FlightTimelineBuildResponse>(
      '/planning-api/v1/flights/timeline',
      payload,
      { baseURL: '' }
    );
    return response.data;
  },

  async getFlightTimeline(sessionId: string): Promise<FlightTimelineBuildResponse> {
    const response = await apiService.api.get<FlightTimelineBuildResponse>(
      `/planning-api/v1/flights/timeline/${sessionId}`,
      { baseURL: '' }
    );
    return response.data;
  },
};

export default maestroChatService;
